import clsx from 'clsx';
import * as React from 'react';
import { type StyleProp, StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import { spacing } from '../constants';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';
import {
    BADGE_HEIGHT,
    BADGE_ICON_SIZE,
    BADGE_KIND_STYLES,
    BADGE_TEXT_SIZE,
    BADGE_V_PADDING,
    type BadgeKind,
    type BadgeSize,
} from './common';

export type BadgeProps = {
    children?: number;
    icon?: IconDefinition;
    kind?: BadgeKind;
    size?: BadgeSize;
    style?: StyleProp<ViewStyle>;
    disabled?: boolean;
};

/**
 * Badge should be for numerical or iconical indicators and shouldn't have any behaviour expectations.
 */
export const Badge = React.forwardRef(function BadgeWithRef(
    props: BadgeProps,
    ref: React.Ref<View>
): React.ReactElement {
    const { icon, kind = 'secondary', size = 'medium', children, disabled, style } = props;
    const [, styles] = useCurrentTheme(BADGE_KIND_STYLES[kind]);
    const [, theme] = useCurrentTheme(themedStyles);

    return (
        <View
            ref={ref}
            style={[
                staticStyles.badge,
                styles.wrapper,
                {
                    minWidth: BADGE_HEIGHT[size],
                    height: BADGE_HEIGHT[size],
                    paddingHorizontal: BADGE_V_PADDING[size],
                },
                disabled ? theme.disabled : null,
                style,
            ]}
        >
            {icon ? (
                <div
                    className={clsx('flex', disabled && 'text-muted')}
                    style={disabled ? undefined : styles.text}
                >
                    {renderIcon(icon, { size: BADGE_ICON_SIZE[size] })}
                </div>
            ) : null}
            {children || children === 0 ? (
                <View>
                    <Text
                        style={[
                            BADGE_TEXT_SIZE[size],
                            styles.text,
                            icon ? spacing.Margin.left100 : undefined,
                        ]}
                    >
                        {children}
                    </Text>
                </View>
            ) : null}
        </View>
    );
});

/**
 * A helper component to use a Badge as an icon.
 * The idea is to allow `className` but not `size`.
 * @deprecated we should not use Badge as an icon because it can be not sized properly.
 */
export function BadgeIconWrapper(props: { className?: string; children: React.ReactNode }) {
    return <div className={clsx('flex', props.className)}>{props.children}</div>;
}

const staticStyles = StyleSheet.create({
    badge: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 100,
        borderWidth: 1,
        width: 'fit-content',
        textAlignVertical: 'center',
        textAlign: 'center',
    },
});

const themedStyles = ThemedStyleSheet.create({
    disabled: (theme) => ({
        backgroundColor: theme.colors.background.muted,
        iconColor: theme.colors.text.muted,
        color: theme.colors.text.muted,
        borderWidth: 0,
        cursor: 'not-allowed',
    }),
});
